"use client";

/**
 * The app-wide home of the card-first trial modal (PANDA-2375).
 *
 * WHY A HOST. Three surfaces mount this modal themselves — the build gate, the
 * credit chip, Settings → Usage — because each already knew the account was
 * gated. Every OTHER upgrade affordance (~20 of them: the workspace header, the
 * mobile sheet, the pricing page, the publish and domain gates, the banners)
 * goes through `openUpgradeEntry`, which is mounted nowhere and can only
 * dispatch. Teaching each of those call sites to own a modal is how the audit
 * found five different trial surfaces in the first place; one listener at the
 * root means they all open the same one.
 *
 * Sibling of <TrialOfferHost/> and <TrialDismissFallback/>, and exactly one of
 * these may be mounted: two listeners would open two modals from one dispatch.
 *
 * The terms come from the dispatch (the server's `card_first_trial` body), so
 * this component never sources a price of its own — and renders nothing at all
 * until a dispatch arrives, rather than holding a placeholder offer.
 */

import { useCallback, useEffect, useState } from "react";
import { CardFirstTrialModal } from "@/components/trial/card-first-trial-modal";
import {
  CARD_FIRST_OPEN_EVENT,
  type CardFirstOpenDetail,
} from "@/lib/trial/upgrade-entry-client";
import { startCardFirstTrialCheckout } from "@/lib/trial/card-first-client";

export function CardFirstTrialHost() {
  const [detail, setDetail] = useState<CardFirstOpenDetail | null>(null);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const next = (event as CustomEvent<CardFirstOpenDetail>).detail;
      // No terms, no modal: the wall's entire job is to state what checkout
      // will do, and a dispatch without them has nothing honest to show.
      if (next?.terms) setDetail(next);
    };
    window.addEventListener(CARD_FIRST_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CARD_FIRST_OPEN_EVENT, onOpen);
  }, []);

  const handleStart = useCallback(async () => {
    // The entry point that opened the wall is carried into checkout, so the
    // funnel can answer "which CTA started this trial?" rather than pooling
    // every door into one name.
    await startCardFirstTrialCheckout(
      detail?.entrypoint ?? "card_first_upgrade_entry",
    );
  }, [detail]);

  if (!detail) return null;

  return (
    <CardFirstTrialModal
      open
      terms={detail.terms}
      onStart={handleStart}
      onClose={() => setDetail(null)}
    />
  );
}
